import { useState, useEffect, useRef, useCallback } from 'react';
import { updateDraft, listAnchors } from '../services/propertyRequestService';
import { toGeometry, buildSearchBoundaries, neighborhoodOverlapsIntersection, fetchIntersectionFromAPI } from '../components/geoScope/geoScopeUtils';
import NeighborhoodsTab   from '../components/geoScope/NeighborhoodsTab';
import CommuteTab         from '../components/geoScope/CommuteTab';
import GeoScopeAnalysis   from '../components/geoScope/GeoScopeAnalysis';
import BoundaryOverlayMap from '../components/geoScope/BoundaryOverlayMap';


export default function Step5_GeoScope({ requestId, onNext, onBack, initialData = {} }) {
    const [activeTab, setActiveTab] = useState('neighborhoods');

    // ── neighborhood selection ────────────────────────────────────────────────
    const [selectedMap, setSelectedMap] = useState(() => {
        const m = new Map();
        // Prefer neighborhoods_data (full objects with boundary) returned by the API,
        // fall back to plain id/name objects from initialData.neighborhoods
        const source = initialData.neighborhoods_data?.length
            ? initialData.neighborhoods_data
            : (initialData.neighborhoods || []);
        source.forEach(item => {
            if (item && typeof item === 'object') m.set(item.id, item);
            else m.set(item, { id: item, name: String(item) });
        });
        return m;
    });

    // ── commute anchors ───────────────────────────────────────────────────────
    const [anchors, setAnchors] = useState([]);
    const [loadingAnchors, setLoadingAnchors] = useState(true);

    // ── boundary sources as STATE so overlay map re-renders ──────────────────
    const [isoPolygons,  setIsoPolygons]  = useState([]);
    const [geoAnalysis,  setGeoAnalysis]  = useState(initialData.geoAnalysis ?? null);
    const [geoLayerCount, setGeoLayerCount] = useState(initialData.geoLayerCount ?? 0);

    // ── all neighborhoods of the selected city (for overlay candidate calc) ───
    const [allNeighborhoods, setAllNeighborhoods] = useState([]);

    // ── candidate neighborhoods ───────────────────────────────────────────────
    const [candidateNbrs, setCandidateNbrs] = useState([]);
    // Store the last intersection so we can recompute candidates when
    // allNeighborhoods loads (city selected) after the intersection is ready
    const [lastIntersection, setLastIntersection] = useState(null);

    const [saving, setSaving] = useState(false);
    const [error,  setError]  = useState('');

    // debounce autosave
    const autosaveTimer = useRef(null);

    useEffect(() => {
        listAnchors(requestId)
            .then(data => setAnchors(data))
            .catch(() => {})
            .finally(() => setLoadingAnchors(false));
    }, [requestId]);

    // ── derived boundary data ─────────────────────────────────────────────────
    const nbrBoundaries = [...selectedMap.values()]
        .map(n => n.boundary ? toGeometry(n.boundary) : null)
        .filter(Boolean);

    const unionMP = buildSearchBoundaries(nbrBoundaries, isoPolygons, geoAnalysis);

    // ── intersection result comes back from BoundaryOverlayMap via API ────────
    const handleIntersectionReady = useCallback((intersectionMP) => {
        setLastIntersection(intersectionMP || null);
    }, []);

    // ── recompute candidates whenever intersection OR neighborhoods change ────
    useEffect(() => {
        if (!lastIntersection || !allNeighborhoods.length) { setCandidateNbrs([]); return; }
        const candidates = allNeighborhoods.filter(n => {
            const geom = toGeometry(n.boundary);
            return geom && neighborhoodOverlapsIntersection(geom, lastIntersection);
        });
        setCandidateNbrs(candidates);
        const ids = candidates.map(n => n.id);
        if (ids.length) {
            updateDraft(requestId, { candidate_neighborhood_ids: ids }).catch(() => {});
        }
    }, [lastIntersection, allNeighborhoods, requestId]); // eslint-disable-line

    // ── autosave neighborhoods whenever selection changes (debounced 1s) ──────
    const nbrSaveTimer = useRef(null);
    useEffect(() => {
        if (!selectedMap.size) return;
        clearTimeout(nbrSaveTimer.current);
        nbrSaveTimer.current = setTimeout(() => {
            updateDraft(requestId, { neighborhoods: [...selectedMap.keys()] }).catch(() => {});
        }, 1000);
        return () => clearTimeout(nbrSaveTimer.current);
    }, [selectedMap, requestId]); // eslint-disable-line

    // ── autosave search_boundaries (debounced 1.5s) ───────────────────────────
    useEffect(() => {
        if (!unionMP) return;
        clearTimeout(autosaveTimer.current);
        autosaveTimer.current = setTimeout(() => {
            updateDraft(requestId, { search_boundaries: unionMP }).catch(() => {});
        }, 1500);
        return () => clearTimeout(autosaveTimer.current);
    }, [unionMP, requestId]); // eslint-disable-line

    // ── save on Next ──────────────────────────────────────────────────────────
    const handleNext = async () => {
        setError('');
        setSaving(true);
        try {
            // 1. Save neighborhoods
            await updateDraft(requestId, { neighborhoods: [...selectedMap.keys()] });

            // 2. Call intersection API explicitly and wait for result
            const intersectionMP = await fetchIntersectionFromAPI(nbrBoundaries, isoPolygons, geoAnalysis);
            setLastIntersection(intersectionMP);

            // 3. Compute candidate neighborhoods from intersection
            let candidateIds = [];
            if (intersectionMP && allNeighborhoods.length) {
                const candidates = allNeighborhoods.filter(n => {
                    const geom = toGeometry(n.boundary);
                    return geom && neighborhoodOverlapsIntersection(geom, intersectionMP);
                });
                setCandidateNbrs(candidates);
                candidateIds = candidates.map(n => n.id);
            }

            // 4. Save search_boundaries + candidate_neighborhood_ids in one PATCH
            const patch = {};
            if (intersectionMP) patch.search_boundaries = intersectionMP;
            if (candidateIds.length) patch.candidate_neighborhood_ids = candidateIds;
            if (Object.keys(patch).length) await updateDraft(requestId, patch);

            onNext({
                neighborhoods: [...selectedMap.values()],
                neighborhoods_data: [...selectedMap.values()],
                commute_anchors: anchors,
                geoAnalysis,
                geoLayerCount,
            });
        } catch (e) {
            setError('خطا در ذخیره اطلاعات. لطفاً دوباره تلاش کنید.');
        } finally {
            setSaving(false);
        }
    };

    // ── layer visibility toggles (for the legend buttons) ────────────────────
    const [visibleLayers, setVisibleLayers] = useState({
        nbr: true, iso: true, geo: true, intersection: true, candidates: true,
    });
    const toggleLayer = key =>
        setVisibleLayers(prev => ({ ...prev, [key]: !prev[key] }));

    const handleGeoAnalysisChange = useCallback((mp) => {
        setGeoAnalysis(mp);
        setGeoLayerCount(mp?.coordinates?.length ?? 0);
    }, []);

    const hasAny = nbrBoundaries.length > 0 || isoPolygons.length > 0 || geoAnalysis !== null;

    return (
        <div>
            <h2 className="prw-step-title">تعیین محدوده جغرافیایی مناسب</h2>
            <p className="prw-step-desc">
                محله‌های مورد نظر، مقاصد رفت‌وآمد و ترجیحات دسترسی را تنظیم کنید.
                نقشه پایین تلاقی همه محدوده‌ها و محله‌های پیشنهادی را بلادرنگ نمایش می‌دهد.
            </p>

            {/* Tabs */}
            <div className="s5gs-tabs">
                <button type="button"
                    className={`s5gs-tab ${activeTab === 'neighborhoods' ? 's5gs-tab-active' : ''}`}
                    onClick={() => setActiveTab('neighborhoods')}>
                    📍 محله‌های مورد نظر
                    {selectedMap.size > 0 && <span className="s5gs-tab-badge">{selectedMap.size}</span>}
                </button>
                <button type="button"
                    className={`s5gs-tab ${activeTab === 'commute' ? 's5gs-tab-active' : ''}`}
                    onClick={() => setActiveTab('commute')}>
                    🚗 مقاصد رفت‌وآمد
                    {anchors.length > 0 && <span className="s5gs-tab-badge">{anchors.length}</span>}
                </button>
                <button type="button"
                    className={`s5gs-tab ${activeTab === 'geoanalysis' ? 's5gs-tab-active' : ''}`}
                    onClick={() => setActiveTab('geoanalysis')}>
                    🗺 تحلیل دسترسی
                    {geoLayerCount > 0 && <span className="s5gs-tab-badge">{geoLayerCount}</span>}
                </button>
            </div>

            <div className="s5gs-tab-content">
                <div style={{ display: activeTab === 'neighborhoods' ? 'block' : 'none' }}>
                    <NeighborhoodsTab
                        selectedMap={selectedMap}
                        setSelectedMap={setSelectedMap}
                        onNeighborhoodsLoaded={setAllNeighborhoods}
                        initialCityId={initialData.neighborhoods_data?.[0]?.city ?? null}
                    />
                </div>
                <div style={{ display: activeTab === 'commute' ? 'block' : 'none' }}>
                    {loadingAnchors
                        ? <p className="prw-step-desc">در حال بارگذاری...</p>
                        : <CommuteTab
                            requestId={requestId}
                            anchors={anchors}
                            setAnchors={setAnchors}
                            onPolygonsChange={setIsoPolygons}
                          />
                    }
                </div>
                <div style={{ display: activeTab === 'geoanalysis' ? 'block' : 'none' }}>
                    <GeoScopeAnalysis
                        onBoundariesChange={handleGeoAnalysisChange}
                        initialGeoAnalysis={initialData.geoAnalysis ?? null}
                    />
                </div>
            </div>

            {/* Live overlay map */}
            {hasAny && (
                <div className="s5gs-overlay-section">
                    <div className="s5gs-overlay-header">
                        <span className="s5gs-overlay-title">🗺 نقشه تلاقی محدوده‌ها</span>
                        <div className="s5gs-overlay-legend">
                            {[
                                { key: 'nbr',          color: '#3949ab', label: 'محله انتخابی' },
                                { key: 'iso',          color: '#fb8c00', label: 'رفت‌وآمد' },
                                { key: 'geo',          color: '#43a047', label: 'دسترسی' },
                                { key: 'intersection', color: '#6a1b9a', label: 'تلاقی' },
                                { key: 'candidates',   color: '#ce93d8', label: 'پیشنهادی' },
                            ].map(({ key, color, label }) => (
                                <button
                                    key={key}
                                    type="button"
                                    className={`s5gs-legend-btn ${visibleLayers[key] ? 's5gs-legend-btn-on' : 's5gs-legend-btn-off'}`}
                                    onClick={() => toggleLayer(key)}
                                    title={visibleLayers[key] ? `پنهان کردن ${label}` : `نمایش ${label}`}
                                >
                                    <span
                                        className="s5gs-legend-dot"
                                        style={{ background: color, opacity: visibleLayers[key] ? 1 : 0.3 }}
                                    />
                                    {label}
                                </button>
                            ))}
                        </div>
                    </div>
                    <BoundaryOverlayMap
                        nbrBoundaries={nbrBoundaries}
                        isoPolygons={isoPolygons}
                        geoAnalysis={geoAnalysis}
                        allNeighborhoods={allNeighborhoods}
                        visibleLayers={visibleLayers}
                        onIntersectionReady={handleIntersectionReady}
                    />
                    {candidateNbrs.length > 0 && (
                        <div className="s5gs-candidates">
                            <p className="s5gs-candidates-title">
                                ✦ محله‌های پیشنهادی ({candidateNbrs.length} محله) — در تلاقی همه محدوده‌ها
                            </p>
                            <div className="s5gs-candidates-chips">
                                {candidateNbrs.map(n => (
                                    <span key={n.id} className="s5gs-candidate-chip">{n.name}</span>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Summary */}
            {(selectedMap.size > 0 || anchors.length > 0 || geoLayerCount > 0) && (
                <div className="s5gs-summary">
                    {selectedMap.size > 0 && <span className="s5gs-summary-item">📍 {selectedMap.size} محله</span>}
                    {anchors.length > 0 && <span className="s5gs-summary-item">🚗 {anchors.length} مقصد</span>}
                    {geoLayerCount > 0 && <span className="s5gs-summary-item">🗺 {geoLayerCount} لایه دسترسی</span>}
                    {candidateNbrs.length > 0 && <span className="s5gs-summary-item">✦ {candidateNbrs.length} محله پیشنهادی</span>}
                </div>
            )}

            {error && <p className="prw-error">{error}</p>}

            <div className="prw-nav">
                <button className="prw-btn prw-btn-secondary" onClick={onBack}>→ مرحله قبل</button>
                <button className="prw-btn prw-btn-primary" onClick={handleNext} disabled={saving}>
                    {saving ? 'در حال ذخیره...' : 'مرحله بعد ←'}
                </button>
            </div>
        </div>
    );
}
